/** Alex tab transport resolution. The tab carries the REAL caller credential (the authed user's Firebase ID token, or
 *  the guest session token); the public static API token is NEVER sent. A shell may leave the conversations-API and
 *  chatroom-WS env vars at their app-relative defaults, so those are honored only when ABSOLUTE. */

import { getEnvironmentConfig } from '@federa/components/lib/environment';
import { CHATS_SERVICE } from '@federa/const/urls-connect';
import { isProductionHostname } from '@federa/utils/config/env-config';
import { isBrowser } from '@federa/utils/browser/detection';
import { CHATS_ASSISTANT_API_PREFIX } from '@federa/const/chats-viewer-routes';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { bearerAuth } from '@federa/const/http-bearer-auth';
import { resolveWsCredential } from './ws-credential.ts';
import type { MessagingTransport } from '@federa/types/ui-chat-messaging';

const isAbsoluteHttp = (value: string | undefined): value is string => Boolean(value && /^https?:\/\//.test(value));
const isAbsoluteWs = (value: string | undefined): value is string => Boolean(value && /^wss?:\/\//.test(value));

/** SSR-safe; used when the shell injected no absolute conversations/WS URL. */
function chatsServiceHttpFallback(): string {
	if (!isBrowser()) return CHATS_SERVICE.DEV_URL;
	return isProductionHostname(window.location.hostname) ? CHATS_SERVICE.PROD_URL : CHATS_SERVICE.DEV_URL;
}
function chatsServiceWsFallback(): string {
	if (!isBrowser()) return CHATS_SERVICE.DEV_WS_URL;
	return isProductionHostname(window.location.hostname) ? CHATS_SERVICE.PROD_WS_URL : CHATS_SERVICE.DEV_WS_URL;
}

/** In the BROWSER always the app's OWN same-origin prefix, forwarded by the host middleware to CHATS_SERVICE. This
 *  OUTRANKS an injected absolute `conversationsApi`: chats-service is workers_dev=false, so that is CROSS-origin. */
function resolveConversationsHttpBase(): string {
	if (isBrowser()) return CHATS_ASSISTANT_API_PREFIX;
	const config = getEnvironmentConfig();
	if (isAbsoluteHttp(config.conversationsApi)) return config.conversationsApi;
	return chatsServiceHttpFallback();
}

/** In the BROWSER always same-origin: the edge route forwards the 101 upgrade to the ChatRoom DO; an absolute override is outranked. */
export function resolveChatRoomWsBase(): string {
	if (isBrowser()) return `wss://${window.location.host}`;
	const config = getEnvironmentConfig();
	if (isAbsoluteWs(config.chatRoomWS)) return config.chatRoomWS;
	return chatsServiceWsFallback();
}

/** Attaches the REAL caller credential. When none resolves -- a guest's first load, before guest-chat-create mints the session token -- NO Authorization header is sent (that bootstrap create is the one unauthenticated guest path); the static token is never used. */
export async function buildAlexTransport(userId: string, username: string | undefined): Promise<MessagingTransport> {
	const credential = await resolveWsCredential(userId, username);
	return {
		base: resolveConversationsHttpBase(),
		headers: credential ? { [STANDARD_HEADERS.AUTHORIZATION]: bearerAuth(credential) } : undefined,
	};
}
